import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import '../common/Mypage.scss';
import '../applications/applications.scss';
import './JobAlerts.scss';
import Loading from '../../../components/Loading';
import { loginStateAtom } from '../../../AtomStore';
import {
  EMPTY_CONDITION,
  describeCondition,
  errorText,
  jobAlertsApi,
  type AlertCondition,
  type AlertOptions,
  type AlertPreview,
  type Dict,
} from './jobAlertsApi';

// 마이페이지 > 이력서 관리 > 공고 알림 섹션 — 조건을 저장하면 맞는 새 공고를 하루 한 번 묶어서 알려준다 (P1)
// 공고 목록의 "이 조건으로 알림 받기"는 /mypage/resumemanage?draft=<JSON> 으로 조건을 미리 채워 이 섹션을 연다.
// (옛 /mypage/alerts 링크는 MypageRouter 에서 쿼리를 유지한 채 이력서 관리로 넘긴다)

const GROUPS: { key: keyof Pick<AlertCondition, 'regions' | 'positions' | 'payBands' | 'employment'>; label: string; optionKey: keyof AlertOptions }[] = [
  { key: 'regions', label: '지역', optionKey: 'regions' },
  { key: 'positions', label: '직분', optionKey: 'positions' },
  { key: 'payBands', label: '사례비', optionKey: 'payBands' },
  { key: 'employment', label: '고용형태', optionKey: 'employment' },
];

function ConditionEditor({
  options,
  initial,
  onCancel,
  onSaved,
}: {
  options: AlertOptions;
  initial: AlertCondition;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [cond, setCond] = useState<AlertCondition>(initial);
  const [preview, setPreview] = useState<AlertPreview | null>(null);
  const [saving, setSaving] = useState(false);

  const toggle = (key: (typeof GROUPS)[number]['key'], code: string) =>
    setCond((prev) => ({ ...prev, [key]: prev[key].includes(code) ? prev[key].filter((c) => c !== code) : [...prev[key], code] }));

  // 선택이 바뀌면 지금 맞는 공고 수를 다시 계산 (최근 60일 게시 중 공고 기준)
  useEffect(() => {
    const empty = GROUPS.every((g) => cond[g.key].length === 0);
    if (empty) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      jobAlertsApi.preview(cond).then(setPreview).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [cond]);

  const save = async () => {
    setSaving(true);
    try {
      if (cond.id) await jobAlertsApi.update(cond.id, cond);
      else await jobAlertsApi.create(cond);
      onSaved();
    } catch (e) {
      alert(errorText(e, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="job-alerts__editor">
      <label className="job-alerts__name">
        <span>이름 (선택)</span>
        <input value={cond.name || ''} maxLength={60} placeholder="예: 서울·경기 부목사" onChange={(e) => setCond({ ...cond, name: e.target.value })} />
      </label>
      {GROUPS.map((g) => (
        <div key={g.key} className="job-alerts__group">
          <span>{g.label}</span>
          <div className="job-alerts__chips">
            {Object.entries(options[g.optionKey] as Dict).map(([code, label]) => (
              <button type="button" key={code} className={cond[g.key].includes(code) ? 'is-on' : ''} onClick={() => toggle(g.key, code)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      ))}
      <p className="job-alerts__hint">선택하지 않은 항목은 "상관없음"으로 봅니다.</p>
      {preview && (
        <div className="job-alerts__preview">
          지금 게시 중인 공고 중 <b>{preview.count}건</b>이 이 조건에 맞습니다.
          {preview.samples.length > 0 && (
            <ul>
              {preview.samples.map((s) => (
                <li key={s.path}>
                  <Link to={s.path}>
                    {s.church ? `${s.church} · ` : ''}
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="app-detail__actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button type="button" className="app-btn" onClick={onCancel}>
          취소
        </button>
        <button type="button" className="app-btn app-btn--primary" disabled={saving} onClick={save}>
          {saving ? '저장 중...' : cond.id ? '조건 수정' : '조건 저장'}
        </button>
      </div>
    </div>
  );
}

export default function JobAlertsPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLogin = useAtomValue(loginStateAtom);
  const [options, setOptions] = useState<AlertOptions | null>(null);
  const [items, setItems] = useState<AlertCondition[]>([]);
  const [lastDigest, setLastDigest] = useState<{ at: string; count: number } | null>(null);
  const [editing, setEditing] = useState<AlertCondition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const sectionRef = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    try {
      const [opts, data] = await Promise.all([jobAlertsApi.options(), jobAlertsApi.list()]);
      setOptions(opts);
      setItems(data.items);
      setLastDigest(data.lastDigest);
      setError('');
    } catch (e) {
      setError(errorText(e, '알림 조건을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 공고 목록에서 넘어온 조건 초안
  useEffect(() => {
    const draft = new URLSearchParams(location.search).get('draft');
    if (!draft) return;
    try {
      setEditing({ ...EMPTY_CONDITION, ...JSON.parse(draft) });
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch {
      // 잘못된 초안은 무시
    }
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // 지원현황 빈 화면의 "조건 저장하고 알림 받기" → #job-alerts 로 들어오면 이 섹션으로 스크롤
  useEffect(() => {
    if (loading || location.hash !== '#job-alerts') return;
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  const toggleActive = async (c: AlertCondition) => {
    try {
      await jobAlertsApi.update(c.id!, { ...c, active: !c.active });
      load();
    } catch (e) {
      alert(errorText(e));
    }
  };

  const remove = async (c: AlertCondition) => {
    if (!window.confirm('이 알림 조건을 삭제할까요?')) return;
    try {
      await jobAlertsApi.remove(c.id!);
      load();
    } catch (e) {
      alert(errorText(e));
    }
  };

  const canAdd = options ? items.length < options.maxConditions : false;

  return (
    <section ref={sectionRef} id="job-alerts" className="job-alerts job-alerts--embedded">
      <div className="mypage-section-head">
        <div>
          <h3 className="mypage-section-title">공고 알림</h3>
          <p className="mypage-section-copy">
            조건을 저장해 두면 맞는 새 청빙 공고를 하루 한 번 묶어서 앱 알림과 이메일로 알려드립니다.
          </p>
        </div>
        {canAdd && !editing && (
          <button type="button" className="app-btn app-btn--primary" onClick={() => setEditing({ ...EMPTY_CONDITION })}>
            + 조건 추가
          </button>
        )}
      </div>

      {!isLogin ? (
        <div className="app-empty">
          <p>로그인 후 이용할 수 있습니다.</p>
          <button type="button" className="app-btn app-btn--primary" onClick={() => navigate('/login')}>
            로그인
          </button>
        </div>
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <Loading />
        </div>
      ) : error ? (
        <div className="app-empty">
          <p>{error}</p>
        </div>
      ) : (
        <>
          {editing && options && (
            <ConditionEditor
              options={options}
              initial={editing}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />
          )}

          {items.length === 0 && !editing ? (
            <div className="app-empty">
              <strong>저장한 알림 조건이 없습니다</strong>
              <p>지역과 직분을 저장해 두면 새 공고가 올라올 때 알려드립니다.</p>
              <button type="button" className="app-btn app-btn--primary" onClick={() => setEditing({ ...EMPTY_CONDITION })}>
                조건 저장하기
              </button>
            </div>
          ) : (
            <ul className="job-alerts__list">
              {items.map((c) => (
                <li key={c.id} className={c.active ? '' : 'is-off'}>
                  <div>
                    <strong>{c.name || describeCondition(c, options)}</strong>
                    {c.name && <p>{describeCondition(c, options)}</p>}
                  </div>
                  <div className="job-alerts__row-actions">
                    <label className="job-alerts__switch">
                      <input type="checkbox" checked={Boolean(c.active)} onChange={() => toggleActive(c)} />
                      {c.active ? '알림 켜짐' : '꺼짐'}
                    </label>
                    <button type="button" className="app-btn app-btn--small" onClick={() => setEditing(c)}>
                      수정
                    </button>
                    <button type="button" className="app-btn app-btn--small app-btn--danger-outline" onClick={() => remove(c)}>
                      삭제
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {lastDigest && (
            <p className="job-alerts__hint">
              마지막 알림: {String(lastDigest.at).slice(0, 10)} · 새 공고 {lastDigest.count}건
            </p>
          )}
          {options && !canAdd && <p className="job-alerts__hint">알림 조건은 최대 {options.maxConditions}개까지 저장할 수 있습니다.</p>}
        </>
      )}
    </section>
  );
}
